(() => {
  if (window.__TP_LUXURY_SEARCH__) return;
  window.__TP_LUXURY_SEARCH__ = true;

  const STORAGE_KEY = 'tp_recent_searches';
  const MAX_RECENT = 5;
  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 260;
  const CACHE_LIMIT = 24;
  const locale = (document.documentElement.lang || 'en').toLowerCase();
  const isArabic = locale.startsWith('ar');
  const cache = new Map();
  const allowedSearchTypes = new Set([
    'product',
    'article',
    'page',
    'article,page',
    'product,article,page'
  ]);

  const normaliseQuery = (value = '') => String(value).trim().replace(/\s+/g, ' ');

  const rootUrl = () => {
    if (window.Shopify?.routes?.root) {
      return Shopify.routes.root.replace(/\/?$/, '/');
    }
    return '/';
  };

  const nativeSearchUrl = (term) => {
    const params = new URLSearchParams();
    params.set('q', normaliseQuery(term));
    params.set('type', 'product');
    params.set('options[prefix]', 'last');
    return `${rootUrl()}search?${params.toString()}`;
  };

  const liveSectionUrl = (term) => {
    const params = new URLSearchParams();
    params.set('q', normaliseQuery(term));
    params.set('type', 'product');
    params.set('options[prefix]', 'last');
    params.set('section_id', 'tp-live-search-results');
    return `${rootUrl()}search?${params.toString()}`;
  };

  const navigateSearch = (rawTerm) => {
    const clean = normaliseQuery(rawTerm);
    if (!clean) return false;
    writeRecent(clean);
    window.location.assign(nativeSearchUrl(clean));
    return true;
  };

  const readRecent = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed)
        ? parsed.map(normaliseQuery).filter(Boolean).slice(0, MAX_RECENT)
        : [];
    } catch (error) {
      return [];
    }
  };

  function writeRecent(term) {
    const clean = normaliseQuery(term);
    if (clean.length < MIN_CHARS) return;

    const lowered = clean.toLocaleLowerCase();
    const current = readRecent().filter((item) => item.toLocaleLowerCase() !== lowered);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([clean, ...current].slice(0, MAX_RECENT)));
    } catch (error) {}
  }

  const ensureHiddenInput = (form, name, value) => {
    let input = form.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
    return input;
  };

  const isSearchForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return false;
    try {
      const pathname = new URL(form.action || '', window.location.origin).pathname;
      return /\/search\/?$/.test(pathname);
    } catch (error) {
      return false;
    }
  };

  const normaliseSearchForm = (form) => {
    if (!isSearchForm(form)) return;

    const typeInput = form.querySelector('input[name="type"]');
    if (!typeInput) {
      ensureHiddenInput(form, 'type', 'product');
    } else if (!allowedSearchTypes.has(normaliseQuery(typeInput.value))) {
      typeInput.value = 'product';
    }

    const prefixInput = form.querySelector('input[name="options[prefix]"]');
    if (!prefixInput) {
      ensureHiddenInput(form, 'options[prefix]', 'last');
    } else if (!['last', 'none'].includes(prefixInput.value)) {
      prefixInput.value = 'last';
    }
  };

  const normaliseSearchForms = (scope = document) => {
    if (scope instanceof HTMLFormElement) normaliseSearchForm(scope);
    scope.querySelectorAll?.('form').forEach(normaliseSearchForm);
  };

  const isolateTpInput = (sourceInput) => {
    if (!sourceInput) return null;
    let input = sourceInput;

    if (input.dataset.tpInputIsolated !== 'true') {
      const clone = input.cloneNode(true);
      clone.dataset.tpInputIsolated = 'true';
      clone.name = 'tp_query';
      input.replaceWith(clone);
      input = clone;
    } else {
      input.name = 'tp_query';
    }

    return input;
  };

  const renderRecent = (scope = document) => {
    scope.querySelectorAll?.('[data-tp-recent-searches]').forEach((wrap) => {
      const list = wrap.querySelector('[data-tp-recent-list]');
      const clear = wrap.querySelector('[data-tp-recent-clear]');
      if (!list) return;

      const searches = readRecent();
      list.replaceChildren();
      wrap.hidden = searches.length === 0;

      searches.forEach((term) => {
        const link = document.createElement('a');
        link.className = 'tp-search-recent__chip';
        link.href = nativeSearchUrl(term);
        link.textContent = term;
        link.dataset.tpSearchTerm = term;
        list.appendChild(link);
      });

      if (clear && clear.dataset.tpSearchBound !== 'true') {
        clear.dataset.tpSearchBound = 'true';
        clear.addEventListener('click', () => {
          try {
            localStorage.removeItem(STORAGE_KEY);
          } catch (error) {}
          renderRecent(document);
        });
      }
    });
  };

  const rememberCache = (query, html) => {
    if (cache.has(query)) cache.delete(query);
    cache.set(query, html);
    while (cache.size > CACHE_LIMIT) {
      cache.delete(cache.keys().next().value);
    }
  };

  const enhanceLiveSearch = (wrap) => {
    if (!wrap || wrap.dataset.tpLiveEnhanced === 'true') return;

    const form = wrap.querySelector('[data-tp-search-form], form[action*="search"], form[role="search"]');
    const sourceInput = wrap.querySelector('[data-tp-search-input], input[name="tp_query"], input[name="q"]');
    const results = wrap.querySelector('[data-tp-live-results]');
    const discovery = wrap.querySelector('[data-tp-search-discovery]');
    const status = wrap.querySelector('[data-tp-search-status]');
    const clear = wrap.querySelector('[data-tp-search-clear]');

    if (!form || !sourceInput || !results) return;

    const input = isolateTpInput(sourceInput);
    if (!input) return;

    wrap.dataset.tpLiveEnhanced = 'true';
    form.dataset.tpSearchForm = '';
    normaliseSearchForm(form);

    let timer = 0;
    let controller = null;
    let requestId = 0;

    const setExpanded = (expanded) => {
      input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    };

    const setLoading = (loading) => {
      wrap.classList.toggle('is-loading', loading);
      results.setAttribute('aria-busy', loading ? 'true' : 'false');
      if (status && loading) status.textContent = isArabic ? 'جارٍ البحث…' : 'Searching…';
    };

    const hideResults = ({ restoreDiscovery = true } = {}) => {
      results.hidden = true;
      results.replaceChildren();
      setExpanded(false);
      if (discovery && restoreDiscovery) discovery.hidden = false;
      if (status) status.textContent = '';
    };

    const showResults = (html, query) => {
      results.innerHTML = html;
      results.hidden = false;
      setExpanded(true);
      if (discovery) discovery.hidden = true;

      const countText = results.querySelector('.tp-search-live-response__count')?.textContent?.trim();
      if (status) {
        status.textContent = countText
          ? `${countText} ${isArabic ? `للبحث عن ${query}` : `for ${query}`}`
          : (isArabic ? `تم تحديث نتائج البحث عن ${query}` : `Search results updated for ${query}`);
      }
    };

    const runSearch = async (rawQuery) => {
      const query = normaliseQuery(rawQuery);
      if (query.length < MIN_CHARS) {
        controller?.abort();
        hideResults();
        setLoading(false);
        return;
      }

      if (cache.has(query)) {
        showResults(cache.get(query), query);
        setLoading(false);
        return;
      }

      const thisRequest = ++requestId;
      controller?.abort();
      controller = new AbortController();
      setLoading(true);

      try {
        const response = await fetch(liveSectionUrl(query), {
          signal: controller.signal,
          credentials: 'same-origin',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'text/html'
          }
        });

        if (!response.ok) throw new Error(`Search request failed: ${response.status}`);

        const html = await response.text();
        if (thisRequest !== requestId) return;

        rememberCache(query, html);
        showResults(html, query);
      } catch (error) {
        if (error.name !== 'AbortError') {
          hideResults({ restoreDiscovery: false });
          if (status) status.textContent = isArabic ? 'تعذر تحديث البحث. حاولي مرة أخرى.' : 'Search could not be updated. Please try again.';
        }
      } finally {
        if (thisRequest === requestId) setLoading(false);
      }
    };

    const syncClear = () => {
      if (clear) clear.hidden = normaliseQuery(input.value).length === 0;
    };

    const resultNavigationLinks = () => [
      ...results.querySelectorAll('.tp-scard__title-link, .tp-search-viewall__link')
    ].filter((link) => !link.hasAttribute('disabled') && link.getClientRects().length > 0);

    const focusResult = (direction, fromElement = document.activeElement) => {
      const links = resultNavigationLinks();
      if (!links.length || results.hidden) return false;

      const current = links.indexOf(fromElement);
      let next;
      if (direction > 0) {
        next = current < 0 ? 0 : (current + 1) % links.length;
      } else {
        next = current < 0 ? links.length - 1 : (current - 1 + links.length) % links.length;
      }

      try {
        links[next].focus({ preventScroll: true });
      } catch (error) {
        links[next].focus();
      }
      links[next].scrollIntoView({ block: 'nearest', inline: 'nearest' });
      return true;
    };

    input.removeAttribute('readonly');
    input.removeAttribute('disabled');
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('enterkeyhint', 'search');
    syncClear();

    input.addEventListener('input', () => {
      syncClear();
      window.clearTimeout(timer);
      const query = normaliseQuery(input.value);
      timer = window.setTimeout(() => runSearch(query), DEBOUNCE_MS);
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        hideResults();
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        const moved = focusResult(event.key === 'ArrowDown' ? 1 : -1, input);
        if (moved) event.preventDefault();
      }
    });

    results.addEventListener('keydown', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        try {
          input.focus({ preventScroll: true });
        } catch (error) {
          input.focus();
        }
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      if (!target.matches('.tp-scard__title-link, .tp-search-viewall__link')) return;

      event.preventDefault();
      focusResult(event.key === 'ArrowDown' ? 1 : -1, target);
    });

    results.addEventListener('click', (event) => {
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (link) writeRecent(input.value);
    });

    if (clear) {
      clear.addEventListener('click', () => {
        window.clearTimeout(timer);
        controller?.abort();
        input.value = '';
        syncClear();
        hideResults();
        try {
          input.focus({ preventScroll: true });
        } catch (error) {
          input.focus();
        }
      });
    }

    wrap.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const chip = target.closest(
        '.tp-search-drawer__chips a, .tp-luxury-search-page__chips a, .tp-search-recent__chip'
      );
      if (!chip || !wrap.contains(chip)) return;

      let term = chip.dataset.tpSearchTerm || '';
      if (!term && chip.getAttribute('href')) {
        try {
          const url = new URL(chip.href, window.location.origin);
          term = url.searchParams.get('q') || '';
        } catch (error) {}
      }

      term = normaliseQuery(term);
      if (term.length < MIN_CHARS) return;

      event.preventDefault();
      input.value = term;
      syncClear();
      writeRecent(term);
      runSearch(term);
      try {
        input.focus({ preventScroll: true });
      } catch (error) {
        input.focus();
      }
    });

    document.addEventListener('click', (event) => {
      if (!wrap.isConnected || results.hidden) return;
      if (!wrap.contains(event.target)) hideResults();
    });
  };

  const init = (scope = document) => {
    normaliseSearchForms(scope);
    renderRecent(scope);
    scope.querySelectorAll?.('[data-tp-live-search]').forEach(enhanceLiveSearch);
  };

  const getTpInputFromForm = (form) => form?.querySelector('[data-tp-search-input]');

  // Own TP submits at capture phase. USF assigns an onsubmit handler that
  // cancels native search; stopping here prevents that handler from running.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-tp-search-form]')) return;

    const input = getTpInputFromForm(form);
    if (!input) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    normaliseSearchForm(form);

    if (!navigateSearch(input.value)) {
      try {
        input.focus({ preventScroll: true });
      } catch (error) {
        input.focus();
      }
    }
  }, true);

  // Own Enter before USF's input keydown handler can cancel/re-route it.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || event.isComposing) return;
    const input = event.target instanceof Element ? event.target.closest('[data-tp-search-input]') : null;
    if (!input) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (!navigateSearch(input.value)) {
      try {
        input.focus({ preventScroll: true });
      } catch (error) {
        input.focus();
      }
    }
  }, true);

  // Own TP search-button clicks before delegated/app search handlers see them.
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const button = target.closest('.tp-search-drawer__submit, .tp-luxury-search-page__submit');
    if (!button) return;

    const form = button.closest('[data-tp-search-form]');
    const input = getTpInputFromForm(form);
    if (!input) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (!navigateSearch(input.value)) {
      try {
        input.focus({ preventScroll: true });
      } catch (error) {
        input.focus();
      }
    }
  }, true);

  // Some legacy Halo code binds directly to input[name="q"] and assumes the
  // field is inside <details>. TP fields are cloned/renamed during init so
  // those listeners cannot own focus or throw on the custom search UI.
  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
